
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Briefcase, Code, GraduationCap, Heart, User, MapPin } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { DynamicFieldArraySection } from "./DynamicFieldArraySection";
import { formatJobType } from "@/utils/formatters";
import {
  BRANCH_OPTIONS,
  QUALIFICATION_OPTIONS,
  getBranchTypeLabel,
  getQualificationTypeLabel,
} from "@/types/enums";

// Use a flexible form data type that matches what's actually used
interface FlexibleCandidateFormData {
  full_name: string;
  email: string;
  phone: string;
  cgpa?: number;
  branch?: string;
  qualification?: string;
  latest_semester_score?: number;
  enrollment_number: string;
  github_url?: string;
  linkedin_url?: string;
  permanent_address?: string;
  permanent_city?: string;
  current_address?: string;
  current_city?: string;
  position_applied?: string;
  key_skills?: string[];
  technologies?: string[];
  hobbies_interests?: string[];
  projects_details?: string[];
  project_links?: string[];
  internships_experience?: string[];
  certifications_awards?: string[];
  event_participations?: string[];
}

interface JobPosition {
  id: string;
  role_name: string;
  description?: string;
  job_type: string;
  package_offered?: string;
}

interface CandidateRegistrationFormProps {
  collegeName: string;
  jobPositions: JobPosition[];
  isLoadingJobPositions: boolean;
  onSubmit: (data: FlexibleCandidateFormData) => void;
  isSubmitting: boolean;
  selectedPosition?: string;
}

export function CandidateRegistrationForm({
  collegeName,
  jobPositions,
  isLoadingJobPositions,
  onSubmit,
  isSubmitting,
  selectedPosition,
}: CandidateRegistrationFormProps) {
  const form = useFormContext<FlexibleCandidateFormData>();

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Position Applied - Show selected position */}
        {selectedPosition && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Selected Position
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-semibold text-blue-900">{selectedPosition}</p>
                <p className="text-sm text-blue-700">
                  You can change this by going back to the position selection step.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="permanent_city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Permanent City *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="current_city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current City</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="permanent_address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Permanent Address *</FormLabel>
                  <FormControl>
                  <Textarea {...field} rows={3} required maxLength={200} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="current_address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Address</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Educational Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Educational Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>College/University</Label>
                <Input value={collegeName} disabled className="bg-gray-100" />
              </div>

              <FormField
                control={form.control}
                name="enrollment_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Enrollment Number *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="qualification"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Qualification</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select qualification" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {QUALIFICATION_OPTIONS.map((qualification) => (
                          <SelectItem key={qualification} value={qualification}>
                            {getQualificationTypeLabel(qualification)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="branch"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Branch/Stream</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select branch" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {BRANCH_OPTIONS.map((branch) => (
                          <SelectItem key={branch} value={branch}>
                            {getBranchTypeLabel(branch)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cgpa"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CGPA (out of 10)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="10"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value
                              ? parseFloat(e.target.value)
                              : undefined
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="latest_semester_score"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Latest Semester Score (%)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value
                              ? parseFloat(e.target.value)
                              : undefined
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Technical Skills */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Technical Skills
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="key_skills"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Key Skills</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={[
                        { label: "JavaScript", value: "javascript" },
                        { label: "Python", value: "python" },
                        { label: "Java", value: "java" },
                        { label: "React", value: "react" },
                        { label: "Node.js", value: "nodejs" },
                        { label: "SQL", value: "sql" },
                        { label: "HTML/CSS", value: "html_css" },
                        { label: "Git", value: "git" },
                      ]}
                      value={field.value || []}
                      onChange={field.onChange}
                      placeholder="Select your key skills..."
                      allowCustom={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="technologies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Technologies</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={[
                        { label: "React", value: "react" },
                        { label: "Angular", value: "angular" },
                        { label: "Vue.js", value: "vuejs" },
                        { label: "Express.js", value: "expressjs" },
                        { label: "MongoDB", value: "mongodb" },
                        { label: "PostgreSQL", value: "postgresql" },
                        { label: "AWS", value: "aws" },
                        { label: "Docker", value: "docker" },
                      ]}
                      value={field.value || []}
                      onChange={field.onChange}
                      placeholder="Select technologies you've worked with..."
                      allowCustom={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Professional Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Professional Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="github_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>GitHub URL</FormLabel>
                    <FormControl>
                      <Input
                        type="url"
                        placeholder="https://github.com/username"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="linkedin_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn URL</FormLabel>
                    <FormControl>
                      <Input
                        type="url"
                        placeholder="https://linkedin.com/in/username"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Show position selector only if no position was pre-selected */}
            {!selectedPosition && (
              <FormField
                control={form.control}
                name="position_applied"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Position Applied For</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={isLoadingJobPositions}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={isLoadingJobPositions ? "Loading positions..." : "Select a position"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {jobPositions.map((position) => (
                          <SelectItem key={position.id} value={position.role_name}>
                            {position.role_name}
                            {position.job_type && ` (${formatJobType(position.job_type)})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </CardContent>
        </Card>

        {/* Personal Interests */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Personal Interests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="hobbies_interests"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hobbies & Interests</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={[
                        { label: "Reading", value: "reading" },
                        { label: "Sports", value: "sports" },
                        { label: "Music", value: "music" },
                        { label: "Photography", value: "photography" },
                        { label: "Gaming", value: "gaming" },
                        { label: "Coding", value: "coding" },
                        { label: "Traveling", value: "traveling" },
                        { label: "Art", value: "art" },
                      ]}
                      value={field.value || []}
                      onChange={field.onChange}
                      placeholder="Select your hobbies and interests..."
                      allowCustom={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
            <CardDescription>
              Add multiple entries for each category below.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <DynamicFieldArraySection
              name="projects_details"
              control={form.control as any}
              label="Project Details"
              addButtonLabel="Add Project"
              placeholder="Describe your project, technologies used, your role, etc."
              rows={3}
            />
            <DynamicFieldArraySection
              name="project_links"
              control={form.control as any}
              label="Project Links"
              addButtonLabel="Add Link"
              placeholder="Portfolio links, GitHub repositories, deployed applications, etc."
            />
            <DynamicFieldArraySection
              name="internships_experience"
              control={form.control as any}
              label="Internships & Experience"
              addButtonLabel="Add Experience"
              placeholder="Previous internships, work experience, roles, responsibilities, etc."
              rows={3}
            />
            <DynamicFieldArraySection
              name="certifications_awards"
              control={form.control as any}
              label="Certifications & Awards"
              addButtonLabel="Add Certification"
              placeholder="List your certifications, awards, achievements, etc."
            />
            <DynamicFieldArraySection
              name="event_participations"
              control={form.control as any}
              label="Event Participations"
              addButtonLabel="Add Event"
              placeholder="Hackathons, competitions, conferences, workshops attended, etc."
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Card>
          <CardContent className="pt-6">
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Complete Registration"}
            </Button>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
}
